import React, { useMemo, useState } from 'react';
import {
  LabRun,
  LabRunStep,
  labPhaseLabel,
  labStepBlockReason,
  labStepStateSentiment,
  LAB_PHASE_SEQUENCE,
} from '../../support/labService';
import { LabDate, LabJsonView, LabStatusTag, labDuration } from './LabShared';

export type LabRunStepsTableProps = {
  run: LabRun;
  steps: LabRunStep[];
  /** step key -> the keys it depends on, from the scenario. Used to say what a block is waiting on. */
  dependencyKeys?: Record<string, string[]>;
  /** Step keys the scenario marks as withholding their output. */
  sensitiveKeys?: string[];
};

/**
 * Every step in a run, in phase order.
 *
 * "Blocked" is the state worth care here. `refresh_step_readiness` has three independent
 * gates - phase, trigger release, dependencies - and all three look identical from outside,
 * so each blocked row says which one it is waiting on instead of leaving somebody to guess.
 */
const LabRunStepsTable: React.FC<LabRunStepsTableProps> = ({
  run,
  steps,
  dependencyKeys = {},
  sensitiveKeys = [],
}) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  const ordered = useMemo(
    () =>
      [...steps].sort(
        (a, b) =>
          LAB_PHASE_SEQUENCE.indexOf(a.phase) - LAB_PHASE_SEQUENCE.indexOf(b.phase) || a.id - b.id,
      ),
    [steps],
  );

  const byKey = useMemo(
    () => Object.fromEntries(steps.map((step) => [step.step_key, step])),
    [steps],
  );

  if (!ordered.length) {
    return <p className="m-0 text-sm text-gray-600">This run has no steps.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2 font-semibold">Step</th>
            <th className="px-3 py-2 font-semibold">Phase</th>
            <th className="px-3 py-2 font-semibold">State</th>
            <th className="px-3 py-2 font-semibold">Attempts</th>
            <th className="px-3 py-2 font-semibold">Finished</th>
            <th className="px-3 py-2 font-semibold">Took</th>
          </tr>
        </thead>

        <tbody>
          {ordered.map((step) => {
            const blockReason = labStepBlockReason(
              step,
              run,
              byKey,
              dependencyKeys[step.step_key] || [],
            );
            const sensitive = sensitiveKeys.includes(step.step_key);
            const isOpen = expanded === step.id;
            const hasDetail = !!(step.output || step.evidence || step.error_message);

            return (
              <React.Fragment key={step.id}>
                <tr
                  className={`border-b border-gray-100 align-top ${
                    hasDetail ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`}
                  onClick={hasDetail ? () => setExpanded(isOpen ? null : step.id) : undefined}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{step.step_key}</div>
                    <div className="text-xs text-gray-500">{step.step_name}</div>

                    {blockReason && (
                      <div className="mt-1 text-xs text-amber-800">{blockReason}</div>
                    )}

                    {step.state === 'skipped' && step.skip_reason && (
                      <div className="mt-1 text-xs text-gray-500">{`Skipped: ${step.skip_reason}`}</div>
                    )}

                    {step.error_message && (
                      <div className="mt-1 text-xs text-red-700">{step.error_message}</div>
                    )}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">{labPhaseLabel(step.phase)}</td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    <LabStatusTag
                      label={step.state}
                      sentiment={labStepStateSentiment(step.state)}
                    />
                    {step.state === 'manual_pending' && (
                      <div className="text-xs text-gray-500">waiting on a person</div>
                    )}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    {step.attempts}
                    {step.consecutive_failures > 1 && (
                      <div className="text-xs text-red-700">
                        {`${step.consecutive_failures} failures in a row`}
                      </div>
                    )}
                    {step.run_count > 1 && (
                      <div className="text-xs text-gray-500">{`ran ${step.run_count} times`}</div>
                    )}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    <LabDate value={step.finished_at} />
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    {step.duration_ms === null || step.duration_ms === undefined
                      ? '-'
                      : labDuration(step.duration_ms / 1000)}
                  </td>
                </tr>

                {isOpen && (
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td className="px-3 py-3" colSpan={6}>
                      {sensitive ? (
                        <p className="m-0 text-sm text-gray-600">
                          Output is withheld for this step - the scenario marks it as echoing
                          something that should not be on a screen.
                        </p>
                      ) : (
                        <div className="flex flex-col gap-3">
                          {step.output && (
                            <div>
                              <div className="text-xs font-semibold uppercase text-gray-500">
                                Output
                              </div>
                              <LabJsonView value={step.output} />
                            </div>
                          )}
                          {step.evidence && (
                            <div>
                              <div className="text-xs font-semibold uppercase text-gray-500">
                                Evidence
                              </div>
                              <LabJsonView value={step.evidence} />
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LabRunStepsTable;
